import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../config/api";

export default function Profile({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const defaultForm = {
    name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    role: "",
    account_type: "",
    status: "",
    provider_options: [],
  };

  const [form, setForm] = useState(defaultForm);

  const fetchProfile = useCallback(async () => {
    const user = JSON.parse(sessionStorage.getItem("user"));

    if (!user) {
      onClose();
      navigate("/");
      return;
    }

    try {
      const res = await fetch(`${API.PROFILE}&id=${user.id}`);
      const data = await res.json();

      if (data.success) {
        let optionsArray = [];

        try {
          optionsArray = JSON.parse(data.user?.provider_options || "[]");
        } catch {
          optionsArray = [];
        }

        setForm({
          name: data.user?.name || "",
          email: data.user?.email || "",
          phone: data.user?.phone || "",
          city: data.user?.city || "",
          state: data.user?.state || "",
          role: data.user?.role || "",
          account_type: data.user?.account_type || "",
          status: data.user?.status || "",
          provider_options: Array.isArray(optionsArray) ? optionsArray : [],
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [navigate, onClose]);

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
    } else {
      setForm(defaultForm);
    }
  }, [isOpen, fetchProfile]);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleProviderChange = (e) => {
    const value = e.target.value;

    setForm((prev) => {
      let updated = [...prev.provider_options];

      if (value === "visitor") {
        updated = updated.includes("visitor") ? [] : ["visitor"];
      } else {
        updated = updated.filter((item) => item !== "visitor");

        if (updated.includes(value)) {
          updated = updated.filter((item) => item !== value);
        } else {
          updated.push(value);
        }
      }

      return {
        ...prev,
        provider_options: updated,
        account_type:
          updated.includes("visitor") || updated.length === 0
            ? "customer"
            : "provider",
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const user = JSON.parse(sessionStorage.getItem("user"));

    try {
      const res = await fetch(API.UPDATE_PROFILE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: user.id,
          ...form,
        }),
      });

      const data = await res.json();
      setLoading(false);

      if (data.success || data.status) {
        sessionStorage.setItem(
          "user",
          JSON.stringify({
            ...user,
            ...form,
          }),
        );

        window.dispatchEvent(new Event("userLogin"));
        alert("Profile Updated Successfully");
        onClose();
      } else {
        alert(data.message || "Update failed");
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
      alert("Failed to update profile");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-popup-overlay" onClick={onClose}>
      <div
        className="profile-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="profile-popup-header">
          <div className="profile-user-box">
            <div className="profile-avatar">
              {form.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div>
              <h3>{form.name || "User Name"}</h3>
              <p>{form.email || "user@email.com"}</p>
            </div>
          </div>

          <button className="profile-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="profile-heading-area">
          <h2>Account Settings</h2>
          <p>Manage your personal details and service preferences</p>
        </div>

        <form onSubmit={handleSubmit} className="profile-popup-form">
          <div className="profile-grid">
            <div className="profile-field">
              <label>Full Name</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="profile-field">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="profile-field">
              <label>Mobile Number</label>
              <input name="phone" value={form.phone} readOnly />
            </div>

            <div className="profile-field">
              <label>City</label>
              <input name="city" value={form.city} onChange={handleChange} />
            </div>

            <div className="profile-field">
              <label>State</label>
              <input name="state" value={form.state} onChange={handleChange} />
            </div>

            <div className="profile-field">
              <label>Designation</label>
              <input name="role" value={form.role} readOnly />
            </div>
          </div>

          <div className="profile-provider-box">
            <h4>What do you provide?</h4>

            <div className="profile-provider-grid">
              {[
                { value: "visitor", label: "I am Visitor" },
                { value: "machines", label: "I want to provide Machines" },
                { value: "designers", label: "I am Designer" },
                {
                  value: "workspaces",
                  label: "I want to give Workspace on Rent",
                },
              ].map((item) => (
                <label key={item.value} className="profile-checkbox-card">
                  <input
                    type="checkbox"
                    value={item.value}
                    checked={form.provider_options.includes(item.value)}
                    onChange={handleProviderChange}
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="profile-footer">
            <button
              type="submit"
              className="profile-save-btn"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
